import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import Button from '@/components/ui/Button';
import { ArrowLeftIcon, PaperAirplaneIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PaperAirplaneIcon as PaperAirplaneSolid, CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { triggerConfettiFromElement } from '@/utils/confetti';
import React from 'react';

interface WireTransfer {
  id: string;
  fromAccount: string;
  toAccount: string;
  toRoutingNumber: string;
  bankCode: string;
  requestedBy: string;
  amount: number;
  currency: string;
  senderLocation: 'in-person' | 'remote';
  isManagerConfirmed: boolean;
  isRecipientConfirmed: boolean;
  isSenderConfirmed: boolean;
  isSent: boolean;
  initiatedDate: Date;
}

interface StatusPillProps {
  label: string;
  completed: boolean;
  onClick?: () => void;
}

const StatusPill = React.forwardRef<HTMLButtonElement, StatusPillProps>(
  ({ label, completed, onClick }, ref) => {
    return (
      <button
        ref={ref}
        onClick={onClick}
        disabled={!onClick}
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          completed
            ? 'text-green-800 bg-green-100 border border-green-200' + (onClick ? ' hover:bg-green-200 cursor-pointer' : '')
            : 'text-gray-600 bg-gray-100 border border-gray-200' + (onClick ? ' hover:bg-gray-200 cursor-pointer' : ' cursor-default')
        }`}
      >
        {completed && <CheckCircleSolid className="mr-1 w-3 h-3" />}
        {label}
      </button>
    );
  }
);

StatusPill.displayName = 'StatusPill';

interface SenderLocationPillProps {
  location: 'in-person' | 'remote';
}

function SenderLocationPill({ location }: SenderLocationPillProps) {
  const isInPerson = location === 'in-person';
  
  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
      isInPerson
        ? 'text-green-800 bg-green-100 border border-green-200'
        : 'text-blue-800 bg-blue-100 border border-blue-200'
    }`}>
      <div className={`w-2 h-2 rounded-full mr-2 ${
        isInPerson ? 'bg-green-600' : 'bg-blue-600'
      }`}></div>
      {isInPerson ? 'In-Person' : 'Remote'}
    </div>
  );
}

export default function WireTransfersPage() {
  const [wireTransfers, setWireTransfers] = useState<WireTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; wire: WireTransfer | null }>({
    isOpen: false,
    wire: null
  });
  
  // Refs to track status pill elements for each wire transfer
  const senderPillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const recipientPillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  
  // Refs to track previous state for each wire transfer
  const previousSenderState = useRef<Map<string, boolean>>(new Map());
  const previousRecipientState = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    console.log('Setting up Firebase listener for wires collection...');

    // Set up real-time listener for wires collection
    const q = query(collection(db, 'wires'), orderBy('initiatedDate', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log('Received Firebase snapshot with', querySnapshot.size, 'wire transfer records');

      const wireData: WireTransfer[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Processing wire transfer document:', doc.id, data);

        wireData.push({
          id: doc.id,
          fromAccount: data.fromAccount,
          toAccount: data.toAccount,
          toRoutingNumber: data.toRoutingNumber,
          bankCode: data.bankCode,
          requestedBy: data.requestedBy,
          amount: data.amount,
          currency: data.currency,
          senderLocation: data.senderLocation || 'in-person', // Default to 'in-person' if not set
          isManagerConfirmed: data.isManagerConfirmed || false,
          isRecipientConfirmed: data.isRecipientConfirmed || false,
          isSenderConfirmed: data.isSenderConfirmed || false,
          isSent: data.isSent || false,
          initiatedDate: data.initiatedDate?.toDate() || new Date(),
        });
      });

      console.log('Processed wire transfer data:', wireData);
      setWireTransfers(wireData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching wire transfers:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Effect to detect isSenderConfirmed and isRecipientConfirmed changes and trigger confetti
  useEffect(() => {
    if (loading) return; // Skip on initial load
    
    wireTransfers.forEach((wire) => {
      // Check Sender Confirmed changes
      const previousSenderValue = previousSenderState.current.get(wire.id);
      const currentSenderValue = wire.isSenderConfirmed;
      
      if (previousSenderValue === false && currentSenderValue === true) {
        const pillElement = senderPillRefs.current.get(wire.id);
        if (pillElement) {
          triggerConfettiFromElement(pillElement);
        }
      }
      previousSenderState.current.set(wire.id, currentSenderValue);
      
      // Check Recipient Confirmed changes
      const previousRecipientValue = previousRecipientState.current.get(wire.id);
      const currentRecipientValue = wire.isRecipientConfirmed;
      
      if (previousRecipientValue === false && currentRecipientValue === true) {
        const pillElement = recipientPillRefs.current.get(wire.id);
        if (pillElement) {
          triggerConfettiFromElement(pillElement);
        }
      }
      previousRecipientState.current.set(wire.id, currentRecipientValue);
    });
  }, [wireTransfers, loading]);

  const updateWireStatus = async (wireId: string, field: 'isManagerConfirmed' | 'isRecipientConfirmed' | 'isSenderConfirmed' | 'isSent', value: boolean) => {
    setUpdating(wireId);
    try {
      const wireRef = doc(db, 'wires', wireId);
      await updateDoc(wireRef, {
        [field]: value,
        lastUpdated: new Date(),
      });
      console.log(`Updated ${field} status for wire transfer ${wireId} to ${value}`);
    } catch (error) {
      console.error(`Error updating ${field} status:`, error);
    } finally {
      setUpdating(null);
    }
  };

  const canSendWire = (wire: WireTransfer) => {
    return wire.isManagerConfirmed && wire.isRecipientConfirmed && wire.isSenderConfirmed && !wire.isSent;
  };

  const handleDeleteWire = async (wireId: string) => {
    setUpdating(wireId);
    try {
      await deleteDoc(doc(db, 'wires', wireId));
      console.log(`Deleted wire transfer ${wireId}`);
    } catch (error) {
      console.error('Error deleting wire transfer:', error);
      alert('Failed to delete wire transfer. Please try again.');
    } finally {
      setUpdating(null);
      setDeleteConfirm({ isOpen: false, wire: null });
    }
  };

  const openDeleteConfirm = (wire: WireTransfer) => {
    setDeleteConfirm({ isOpen: true, wire });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ isOpen: false, wire: null });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatRelativeDate = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInMs / (1000 * 60));
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto mb-4 loading-spinner" />
          <p className="text-gray-600">Loading wire transfers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="py-6 container-centered">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeftIcon className="mr-2 w-4 h-4" />
                  Back to Application
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Wire Transfers</h1>
                <p className="text-gray-600">Manage wire transfer requests</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {wireTransfers.length} wire transfer{wireTransfers.length !== 1 ? 's' : ''}
              </div>
              <Link to="/wire-transfers/add">
                <Button className="text-white bg-blue-600 hover:bg-blue-700">
                  <PlusIcon className="mr-2 w-4 h-4" />
                  Add New Wire Transfer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="py-8 container-centered">
        {wireTransfers.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <PaperAirplaneIcon className="mx-auto w-12 h-12" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">No wire transfers</h3>
            <p className="mb-4 text-gray-600">Wire transfer requests will appear here after they are submitted.</p>
            <Link to="/">
              <Button>Back to Application</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {wireTransfers.map((wire) => (
              <div key={wire.id} className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-4 space-x-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {wire.requestedBy}
                        </h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <span>Initiated</span>
                          <span
                            className="font-medium"
                            title={wire.initiatedDate.toLocaleString()}
                          >
                            {formatRelativeDate(wire.initiatedDate)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatCurrency(wire.amount, wire.currency)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {wire.currency}
                        </div>
                      </div>
                    </div>

                    {/* Sender Location Pill */}
                    <div className="mb-4">
                      <SenderLocationPill location={wire.senderLocation} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-500">From Account:</span>
                          <div className="font-mono text-sm text-gray-900">{wire.fromAccount}</div>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">To Account:</span>
                          <div className="font-mono text-sm text-gray-900">{wire.toAccount}</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Routing Number:</span>
                          <div className="font-mono text-sm text-gray-900">{wire.toRoutingNumber}</div>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Bank Code:</span>
                          <div className="text-sm text-gray-900">{wire.bankCode}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 md:mt-0 md:ml-6">
                    <div className="flex flex-col space-y-3">
                      {/* Status Pills */}
                      <div className="flex flex-wrap gap-2">
                        <StatusPill
                          label="Manager Confirmed"
                          completed={wire.isManagerConfirmed}
                          onClick={() => updateWireStatus(wire.id, 'isManagerConfirmed', !wire.isManagerConfirmed)}
                        />
                        <StatusPill
                          ref={(el) => {
                            if (el) {
                              recipientPillRefs.current.set(wire.id, el);
                            } else {
                              recipientPillRefs.current.delete(wire.id);
                            }
                          }}
                          label="Recipient Confirmed"
                          completed={wire.isRecipientConfirmed}
                          onClick={() => updateWireStatus(wire.id, 'isRecipientConfirmed', !wire.isRecipientConfirmed)}
                        />
                        <StatusPill
                          ref={(el) => {
                            if (el) {
                              senderPillRefs.current.set(wire.id, el);
                            } else {
                              senderPillRefs.current.delete(wire.id);
                            }
                          }}
                          label="Sender Confirmed"
                          completed={wire.isSenderConfirmed}
                          onClick={() => updateWireStatus(wire.id, 'isSenderConfirmed', !wire.isSenderConfirmed)}
                        />
                        <StatusPill label="Sent" completed={wire.isSent} />
                      </div>

                      {/* Send Button */}
                      <Button
                        onClick={() => updateWireStatus(wire.id, 'isSent', true)}
                        disabled={!canSendWire(wire) || updating === wire.id}
                        className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          canSendWire(wire)
                            ? 'text-white bg-blue-600 hover:bg-blue-700'
                            : 'text-gray-500 bg-gray-300 cursor-not-allowed'
                        }`}
                        size="sm"
                      >
                        {updating === wire.id ? (
                          'Sending...'
                        ) : (
                          <>
                            <PaperAirplaneSolid className="mr-2 w-4 h-4" />
                            Send Wire
                          </>
                        )}
                      </Button>
                      
                      {/* Delete Wire Link */}
                      <button
                        onClick={() => openDeleteConfirm(wire)}
                        disabled={updating === wire.id}
                        className="text-sm text-red-600 hover:text-red-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Delete Wire
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm.isOpen && deleteConfirm.wire && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 mx-4 w-full max-w-md bg-white rounded-lg">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="mr-3 w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Wire Transfer</h3>
            </div>
            
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete the wire transfer for{' '}
              <strong>{formatCurrency(deleteConfirm.wire.amount, deleteConfirm.wire.currency)}</strong>
              {' '}from account <strong>{deleteConfirm.wire.fromAccount}</strong>?
              This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={closeDeleteConfirm}
                disabled={updating === deleteConfirm.wire.id}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDeleteWire(deleteConfirm.wire!.id)}
                disabled={updating === deleteConfirm.wire.id}
                className="text-white bg-red-600 border-red-600 hover:bg-red-700"
              >
                {updating === deleteConfirm.wire.id ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
